'use client';

import React from 'react';
import { useParams } from 'next/navigation';
import { PulseButton, PulseIconButton } from 'src/components';
import { faEnvelope } from '@fortawesome/pro-solid-svg-icons';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useSendInviteEmailMutation } from 'src/api/redux';
interface ResendInviteButtonProps {
  userId: string;
  variant?: 'regular' | 'compact' | 'icon';
}

const ResendInviteButton = ({
  userId,
  variant = 'regular',
}: ResendInviteButtonProps) => {
  const { companyId } = useParams<{ companyId: string }>();
  const log = useLogging();
  const { setNotification } = useNotification();

  const [sendInviteEmail, { isLoading }] = useSendInviteEmailMutation();

  const handleSendInvite = async () => {
    try {
      await sendInviteEmail({ companyId, userId }).unwrap();
      setNotification({
        title: 'Success',
        message: 'Invite resent',
        type: 'success',
      });
    } catch (err: any) {
      log.exception('Error resending invite', {
        tags: {
          userId,
          error: err,
        },
      });

      setNotification({
        title: 'Error',
        message:
          err?.data?.error_message || err?.message || 'Error resending invite',
        type: 'error',
      });
    }
  };

  switch (variant) {
    case 'regular': {
      return (
        <PulseButton
          loading={isLoading}
          size="sm"
          variant="outline"
          color="primary"
          onClick={handleSendInvite}
          startDecorator={faEnvelope}
          aria-label="Resend invite email"
          data-testid="resend-invite-button"
        >
          Resend Invite
        </PulseButton>
      );
    }

    case 'compact': {
      return (
        <PulseIconButton
          loading={isLoading}
          size="sm"
          variant="soft"
          color="primary"
          onClick={handleSendInvite}
          icon={faEnvelope}
          tooltip="Resend Invite"
          aria-label="Resend invite email"
        />
      );
    }

    case 'icon': {
      return (
        <PulseIconButton
          loading={isLoading}
          size="sm"
          // `plain` to match the deactivate and impersonate icons sharing the
          // row-hover action bar; `soft` gave these two a filled background.
          variant="plain"
          onClick={handleSendInvite}
          icon={faEnvelope}
          className="text-neutral-500 hover:text-sky-500"
          tooltip="Resend Invite"
          aria-label="Resend invite email"
        />
      );
    }

    default:
      return null;
  }
};

export default ResendInviteButton;
